import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  RefreshCw,
  Clock,
  Globe,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Calendar,
  ChevronRight,
  Settings,
} from 'lucide-react';
import { api } from '../services/api';
import type { ProjectSchedule, ScheduleFrequency } from '../types';
import { formatDistanceToNow, format, isToday, isTomorrow, addDays } from 'date-fns';
import { de } from 'date-fns/locale';
import clsx from 'clsx';

const WEEKDAY_FULL = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const FREQUENCY_LABELS: Record<ScheduleFrequency, string> = {
  daily: 'Täglich',
  weekly: 'Wöchentlich',
  monthly: 'Monatlich',
};

function formatHour(hour: number | null): string {
  if (hour === null) return '--:00';
  return `${hour.toString().padStart(2, '0')}:00`;
}

function formatScheduleShort(schedule: ProjectSchedule): string {
  if (!schedule.enabled || !schedule.frequency) return 'Deaktiviert';

  switch (schedule.frequency) {
    case 'daily':
      return `Täglich ${formatHour(schedule.hour)}`;
    case 'weekly':
      return `${WEEKDAY_FULL[schedule.weekday ?? 0]} ${formatHour(schedule.hour)}`;
    case 'monthly':
      return `${schedule.day ?? 1}. des Monats ${formatHour(schedule.hour)}`;
    default:
      return 'Unbekannt';
  }
}

function getNextBackupDate(schedule: ProjectSchedule): Date | null {
  if (!schedule.enabled || !schedule.nextScheduled) return null;
  return new Date(schedule.nextScheduled);
}

function formatNextBackup(date: Date | null): string {
  if (!date) return '-';
  if (isToday(date)) return `Heute ${format(date, 'HH:mm')}`;
  if (isTomorrow(date)) return `Morgen ${format(date, 'HH:mm')}`;
  return format(date, 'EEE dd.MM. HH:mm', { locale: de });
}

export default function Schedules() {
  const [frequencyFilter, setFrequencyFilter] = useState<ScheduleFrequency | undefined>();
  const [showEditModal, setShowEditModal] = useState<ProjectSchedule | null>(null);
  const queryClient = useQueryClient();

  const { data: schedules, isLoading: schedulesLoading, dataUpdatedAt } = useQuery({
    queryKey: ['schedules', frequencyFilter],
    queryFn: () => api.schedules.list(frequencyFilter),
  });

  const syncMutation = useMutation({
    mutationFn: () => api.schedules.sync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  // Auto-sync if data is older than 1 hour
  useEffect(() => {
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    const hasOldData = schedules?.some(s => {
      if (!s.lastSyncAt) return true;
      return new Date(s.lastSyncAt).getTime() < oneHourAgo;
    });

    if (hasOldData && !syncMutation.isPending) {
      syncMutation.mutate();
    }
  }, [schedules]);

  // Group schedules
  const enabledSchedules = schedules?.filter(s => s.enabled) || [];
  const disabledSchedules = schedules?.filter(s => !s.enabled) || [];

  // Sort by next scheduled
  const sortedEnabled = [...enabledSchedules].sort((a, b) => {
    const dateA = getNextBackupDate(a);
    const dateB = getNextBackupDate(b);
    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;
    return dateA.getTime() - dateB.getTime();
  });

  // Find conflicts (same hour)
  const hourGroups: Record<string, ProjectSchedule[]> = {};
  for (const s of enabledSchedules) {
    if (s.frequency === 'daily') {
      const key = `daily-${s.hour}`;
      if (!hourGroups[key]) hourGroups[key] = [];
      hourGroups[key].push(s);
    } else if (s.frequency === 'weekly') {
      const key = `weekly-${s.weekday}-${s.hour}`;
      if (!hourGroups[key]) hourGroups[key] = [];
      hourGroups[key].push(s);
    }
  }
  const conflicts = Object.entries(hourGroups).filter(([_, items]) => items.length >= 3);

  // Upcoming backups (next 7 days)
  const now = new Date();
  const next7Days = addDays(now, 7);
  const upcomingBackups = sortedEnabled
    .map(s => ({ schedule: s, date: getNextBackupDate(s) }))
    .filter(item => item.date && item.date >= now && item.date <= next7Days)
    .slice(0, 10);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Zeitpläne</h1>
          <p className="text-gray-600 mt-1">
            {enabledSchedules.length} aktive Zeitpläne, {disabledSchedules.length} deaktiviert
          </p>
        </div>

        <button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            'bg-primary-600 text-white hover:bg-primary-700',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <RefreshCw className={clsx('w-4 h-4', syncMutation.isPending && 'animate-spin')} />
          {syncMutation.isPending ? 'Synchronisiere...' : 'Synchronisieren'}
        </button>
      </div>

      {/* Sync notification */}
      {syncMutation.isSuccess && (
        <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800 text-sm">
            Synchronisiert: {syncMutation.data.synced} erfolgreich, {syncMutation.data.failed} fehlgeschlagen
          </span>
        </div>
      )}

      {schedulesLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main list - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Conflicts warning */}
            {conflicts.length > 0 && (
              <div className="card p-4 border-yellow-300 bg-yellow-50">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-yellow-800">Backup-Konflikte erkannt</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      {conflicts.length} Zeitfenster mit 3+ gleichzeitigen Backups.
                      Dies kann zu Performance-Problemen führen.
                    </p>
                    <div className="mt-2 space-y-1">
                      {conflicts.slice(0, 3).map(([key, items]) => (
                        <div key={key} className="text-sm text-yellow-700">
                          <span className="font-medium">{items.length} Backups</span> um{' '}
                          {formatHour(items[0].hour)}
                          {items[0].frequency === 'weekly' && ` (${WEEKDAY_FULL[items[0].weekday ?? 0]})`}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filter */}
            <div className="card p-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">Filter:</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFrequencyFilter(undefined)}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                      !frequencyFilter
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    )}
                  >
                    Alle
                  </button>
                  {(['daily', 'weekly', 'monthly'] as const).map((freq) => (
                    <button
                      key={freq}
                      onClick={() => setFrequencyFilter(freq)}
                      className={clsx(
                        'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                        frequencyFilter === freq
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      )}
                    >
                      {FREQUENCY_LABELS[freq]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Active schedules table */}
            <div className="card overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h2 className="font-semibold flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Aktive Zeitpläne ({sortedEnabled.length})
                </h2>
              </div>
              {sortedEnabled.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Keine aktiven Zeitpläne gefunden
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Projekt
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Zeitplan
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Nächstes Backup
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Zeitzone
                      </th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sortedEnabled.map((schedule) => {
                      const nextDate = getNextBackupDate(schedule);
                      const isConflict = conflicts.some(([_, items]) =>
                        items.some(s => s.projectId === schedule.projectId)
                      );

                      return (
                        <tr key={schedule.projectId} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <Link
                              to={`/projects/${schedule.projectId}`}
                              className="font-medium text-primary-600 hover:text-primary-800 flex items-center gap-1"
                            >
                              {schedule.projectName}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className={clsx(
                                'px-2 py-0.5 rounded text-xs font-medium',
                                schedule.frequency === 'daily' && 'bg-blue-100 text-blue-700',
                                schedule.frequency === 'weekly' && 'bg-purple-100 text-purple-700',
                                schedule.frequency === 'monthly' && 'bg-orange-100 text-orange-700'
                              )}>
                                {FREQUENCY_LABELS[schedule.frequency!]}
                              </span>
                              <span className="text-sm text-gray-600">
                                {formatScheduleShort(schedule)}
                              </span>
                              {isConflict && (
                                <span title="Konflikt">
                                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {nextDate ? (
                              <span className={clsx(
                                isToday(nextDate) && 'text-green-600 font-medium'
                              )}>
                                {formatNextBackup(nextDate)}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              {schedule.timezone || 'UTC'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setShowEditModal(schedule)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                              title="Zeitplan bearbeiten"
                            >
                              <Settings className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Disabled schedules */}
            {disabledSchedules.length > 0 && (
              <div className="card overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h2 className="font-semibold flex items-center gap-2 text-gray-600">
                    <XCircle className="w-4 h-4 text-gray-400" />
                    Deaktivierte Zeitpläne ({disabledSchedules.length})
                  </h2>
                </div>
                <div className="divide-y">
                  {disabledSchedules.map((schedule) => (
                    <div key={schedule.projectId} className="px-4 py-3 flex items-center justify-between">
                      <Link
                        to={`/projects/${schedule.projectId}`}
                        className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
                      >
                        {schedule.projectName}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                      <button
                        onClick={() => setShowEditModal(schedule)}
                        className="text-sm text-primary-600 hover:text-primary-800"
                      >
                        Aktivieren
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - 1 column */}
          <div className="space-y-6">
            {/* Upcoming backups */}
            <div className="card p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary-600" />
                Nächste Backups
              </h3>
              {upcomingBackups.length === 0 ? (
                <p className="text-sm text-gray-500">Keine Backups in den nächsten 7 Tagen</p>
              ) : (
                <div className="space-y-3">
                  {upcomingBackups.map(({ schedule, date }) => (
                    <Link
                      key={schedule.projectId}
                      to={`/projects/${schedule.projectId}`}
                      className="block p-2 -mx-2 rounded hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {schedule.projectName}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {formatNextBackup(date)}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Quick stats */}
            <div className="card p-4">
              <h3 className="font-semibold mb-4">Übersicht</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Täglich</span>
                  <span className="font-medium">
                    {enabledSchedules.filter(s => s.frequency === 'daily').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Wöchentlich</span>
                  <span className="font-medium">
                    {enabledSchedules.filter(s => s.frequency === 'weekly').length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Monatlich</span>
                  <span className="font-medium">
                    {enabledSchedules.filter(s => s.frequency === 'monthly').length}
                  </span>
                </div>
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Konflikte</span>
                    <span className={clsx(
                      'font-medium',
                      conflicts.length > 0 ? 'text-yellow-600' : 'text-green-600'
                    )}>
                      {conflicts.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Last sync info */}
            {dataUpdatedAt && (
              <div className="text-xs text-gray-400 text-center">
                Letzte Aktualisierung:{' '}
                {formatDistanceToNow(dataUpdatedAt, { addSuffix: true, locale: de })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Schedule Modal */}
      {showEditModal && (
        <EditScheduleModal
          schedule={showEditModal}
          onClose={() => setShowEditModal(null)}
        />
      )}
    </div>
  );
}

// Edit Schedule Modal
function EditScheduleModal({
  schedule,
  onClose,
}: {
  schedule: ProjectSchedule;
  onClose: () => void;
}) {
  const [enabled, setEnabled] = useState(schedule.enabled);
  const [frequency, setFrequency] = useState<ScheduleFrequency>(schedule.frequency || 'weekly');
  const [weekday, setWeekday] = useState(schedule.weekday ?? 0);
  const [day, setDay] = useState(schedule.day ?? 1);
  const [hour, setHour] = useState(schedule.hour ?? 3);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const queryClient = useQueryClient();

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      await api.projects.updateConfig(schedule.projectId, {
        schedule_enabled: enabled,
        schedule_frequency: frequency,
        schedule_weekday: weekday,
        schedule_day: day,
        schedule_hour: hour,
      });

      setSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      setTimeout(() => onClose(), 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold mb-2">Zeitplan bearbeiten</h2>
        <p className="text-gray-600 mb-6">{schedule.projectName}</p>

        <div className="space-y-4">
          {/* Enabled toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="font-medium">Automatisches Backup aktiviert</span>
          </label>

          {enabled && (
            <>
              {/* Frequency */}
              <div>
                <label className="label">Häufigkeit</label>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value as ScheduleFrequency)}
                  className="input"
                >
                  <option value="daily">Täglich</option>
                  <option value="weekly">Wöchentlich</option>
                  <option value="monthly">Monatlich</option>
                </select>
              </div>

              {/* Weekday (for weekly) */}
              {frequency === 'weekly' && (
                <div>
                  <label className="label">Wochentag</label>
                  <select
                    value={weekday}
                    onChange={(e) => setWeekday(parseInt(e.target.value))}
                    className="input"
                  >
                    {WEEKDAY_FULL.map((name, idx) => (
                      <option key={idx} value={idx}>{name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Day (for monthly) */}
              {frequency === 'monthly' && (
                <div>
                  <label className="label">Tag des Monats</label>
                  <select
                    value={day}
                    onChange={(e) => setDay(parseInt(e.target.value))}
                    className="input"
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}.</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Hour */}
              <div>
                <label className="label">Uhrzeit</label>
                <select
                  value={hour}
                  onChange={(e) => setHour(parseInt(e.target.value))}
                  className="input"
                >
                  {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                    <option key={h} value={h}>{formatHour(h)}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Gespeichert!
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end mt-6">
          <button onClick={onClose} className="btn btn-secondary">
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
